import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Building2, UserPlus, ShoppingCart, ChevronRight, X, Sparkles, Copy } from "lucide-react";

const STEPS = [
  { id: "restaurant", label: "Restaurant",     icon: Building2,    desc: "Voeg je eerste locatie toe" },
  { id: "team",       label: "Team",           icon: UserPlus,     desc: "Nodig een chef uit" },
  { id: "order",      label: "Eerste actie",   icon: ShoppingCart, desc: "Bekijk je dashboard" },
];

export default function OnboardingWizard({ onClose }) {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  // Step state
  const [restaurant, setRestaurant] = useState({ name: "", location: "" });
  const [createdRestaurantId, setCreatedRestaurantId] = useState(null);
  const [createdInviteCode, setCreatedInviteCode] = useState(null);
  const [chef, setChef] = useState({ name: "", email: "", password: "" });
  const [chefCreated, setChefCreated] = useState(false);
  const [copied, setCopied] = useState(false);

  // Skip step 1 if user already has restaurants
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/restaurants");
        if (r.data.length > 0) {
          setCreatedRestaurantId(r.data[0].id);
          setCreatedInviteCode(r.data[0].invite_code);
          setStep((s) => Math.max(s, 1));
        }
        // Skip step 2 if team has more than admin
        const t = await api.get("/team");
        if (t.data.filter((u) => u.role === "chef").length > 0) {
          setStep((s) => Math.max(s, 2));
        }
      } catch { /* ignore */ }
    })();
  }, []);

  const dismiss = () => {
    localStorage.setItem("stockly_onboarding_dismissed", "1");
    onClose?.();
  };

  const submitRestaurant = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/restaurants", restaurant);
      setCreatedRestaurantId(data.id);
      setCreatedInviteCode(data.invite_code);
      toast.success("Restaurant toegevoegd!");
      setStep(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Mislukt");
    } finally { setBusy(false); }
  };

  const submitChef = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/team", {
        ...chef,
        role: "chef",
        restaurant_id: createdRestaurantId || null,
      });
      setChefCreated(true);
      toast.success(`Chef ${chef.name} uitgenodigd!`);
      setTimeout(() => setStep(2), 600);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Mislukt");
    } finally { setBusy(false); }
  };

  const finish = () => {
    localStorage.setItem("stockly_onboarding_done", "1");
    onClose?.();
    refreshUser?.();
  };

  const copyInvite = async () => {
    if (!createdInviteCode) return;
    try {
      await navigator.clipboard.writeText(createdInviteCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="onboarding-wizard">
      <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="relative bg-gradient-to-br from-[#1B2D5C] to-[#142244] p-6 sm:p-8 text-white">
          <button onClick={dismiss} className="absolute top-4 right-4 text-white/60 hover:text-white p-1" data-testid="onboarding-dismiss" aria-label="Sluiten">
            <X size={18} />
          </button>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={18} className="text-[#5BC044]" />
            <span className="overline text-white/70 !text-[#5BC044]">Welkom bij Stockly</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
            Hoi {user?.name?.split(" ")[0] || "daar"}, drie korte stappen.
          </h2>
          <p className="text-sm text-white/70 mt-2 max-w-md">
            We zetten je organisatie in een paar minuten klaar zodat je direct kunt bestellen.
          </p>

          {/* Step indicator */}
          <div className="flex items-center gap-3 mt-6">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const completed = i < step;
              const active = i === step;
              return (
                <div key={s.id} className="flex items-center gap-3 flex-1" data-testid={`step-indicator-${s.id}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center border transition-colors ${completed ? "bg-[#5BC044] border-[#5BC044]" : active ? "bg-white text-[#1B2D5C] border-white" : "bg-white/10 border-white/20 text-white/40"}`}>
                    {completed ? <Check size={16} strokeWidth={2.5} /> : <Icon size={16} strokeWidth={1.8} />}
                  </div>
                  {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 ${completed ? "bg-[#5BC044]" : "bg-white/15"}`} />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step body */}
        <div className="p-6 sm:p-8">
          {step === 0 && (
            <form onSubmit={submitRestaurant} className="space-y-4" data-testid="step-restaurant">
              <div>
                <div className="overline mb-1">Stap 1 van 3</div>
                <h3 className="text-xl font-bold tracking-tight text-[#1B2D5C]">Voeg je eerste restaurant toe</h3>
                <p className="text-sm text-zinc-500 mt-1">Je kunt later meer locaties toevoegen via Restaurants.</p>
              </div>
              <div>
                <label className="overline block mb-1.5">Naam</label>
                <input required autoFocus value={restaurant.name} onChange={(e) => setRestaurant({ ...restaurant, name: e.target.value })}
                  className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#5BC044]/40 focus:border-[#5BC044]"
                  placeholder="Brasserie Centrum"
                  data-testid="onb-restaurant-name" />
              </div>
              <div>
                <label className="overline block mb-1.5">Locatie</label>
                <input required value={restaurant.location} onChange={(e) => setRestaurant({ ...restaurant, location: e.target.value })}
                  className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#5BC044]/40 focus:border-[#5BC044]"
                  placeholder="Amsterdam"
                  data-testid="onb-restaurant-location" />
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={dismiss} className="px-4 h-11 text-sm text-zinc-500 hover:text-zinc-900" data-testid="onb-skip-all">Later doen</button>
                <button type="submit" disabled={busy} className="ml-auto inline-flex items-center gap-2 h-11 px-5 bg-[#5BC044] hover:bg-[#4FAA3A] disabled:opacity-50 text-white rounded-md text-sm font-semibold" data-testid="onb-restaurant-submit">
                  {busy ? "Bezig…" : <>Volgende <ChevronRight size={15} /></>}
                </button>
              </div>
            </form>
          )}

          {step === 1 && !chefCreated && (
            <form onSubmit={submitChef} className="space-y-4" data-testid="step-team">
              <div>
                <div className="overline mb-1">Stap 2 van 3</div>
                <h3 className="text-xl font-bold tracking-tight text-[#1B2D5C]">Nodig je eerste chef uit</h3>
                <p className="text-sm text-zinc-500 mt-1">Chefs plaatsen bestellingen en beheren hun voorraad. Je kunt dit altijd later doen.</p>
              </div>
              {createdInviteCode && (
                <div className="card-flat bg-emerald-50 border-emerald-200 p-3 flex items-center gap-3">
                  <div className="overline text-emerald-700 text-[10px]">Deelcode restaurant</div>
                  <code className="font-mono font-bold text-emerald-800 tracking-wider flex-1">{createdInviteCode}</code>
                  <button type="button" onClick={copyInvite} className="text-emerald-700 hover:text-emerald-900 p-1" data-testid="onb-copy-invite">
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-1.5">Naam</label>
                  <input required value={chef.name} onChange={(e) => setChef({ ...chef, name: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#5BC044]/40 focus:border-[#5BC044]"
                    placeholder="Jan de Vries"
                    data-testid="onb-chef-name" />
                </div>
                <div>
                  <label className="overline block mb-1.5">E-mail</label>
                  <input required type="email" value={chef.email} onChange={(e) => setChef({ ...chef, email: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#5BC044]/40 focus:border-[#5BC044]"
                    placeholder="chef@restaurant.nl"
                    data-testid="onb-chef-email" />
                </div>
              </div>
              <div>
                <label className="overline block mb-1.5">Tijdelijk wachtwoord <span className="text-zinc-400 normal-case font-normal">(deel veilig met je chef)</span></label>
                <input required minLength={6} type="text" value={chef.password} onChange={(e) => setChef({ ...chef, password: e.target.value })}
                  className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#5BC044]/40 focus:border-[#5BC044]"
                  placeholder="welkom2026"
                  data-testid="onb-chef-password" />
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setStep(2)} className="px-4 h-11 text-sm text-zinc-500 hover:text-zinc-900" data-testid="onb-skip-team">Overslaan</button>
                <button type="submit" disabled={busy} className="ml-auto inline-flex items-center gap-2 h-11 px-5 bg-[#5BC044] hover:bg-[#4FAA3A] disabled:opacity-50 text-white rounded-md text-sm font-semibold" data-testid="onb-chef-submit">
                  {busy ? "Bezig…" : <>Uitnodigen <ChevronRight size={15} /></>}
                </button>
              </div>
            </form>
          )}

          {step === 1 && chefCreated && (
            <div className="text-center py-6">
              <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-3"><Check size={24} strokeWidth={2.5} /></div>
              <h3 className="text-lg font-bold text-[#1B2D5C]">Chef uitgenodigd!</h3>
              <p className="text-sm text-zinc-500 mt-1">Door naar de laatste stap…</p>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="step-finish">
              <div>
                <div className="overline mb-1">Stap 3 van 3</div>
                <h3 className="text-xl font-bold tracking-tight text-[#1B2D5C]">Je bent klaar om te starten</h3>
                <p className="text-sm text-zinc-500 mt-1">Hier zijn de drie meest gebruikte plekken in Stockly:</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <ShortcutCard onClick={() => { nav("/producten"); finish(); }} testid="onb-go-products" icon="📦" title="Producten" desc="Beheer catalogus en prijzen" />
                <ShortcutCard onClick={() => { nav("/voorraad"); finish(); }} testid="onb-go-inventory" icon="🗃️" title="Voorraad" desc="Zet minima om alerts te krijgen" />
                <ShortcutCard onClick={() => { nav("/bestellingen"); finish(); }} testid="onb-go-orders" icon="🛒" title="Bestellingen" desc="Bekijk en exporteer als PDF" />
              </div>
              <div className="flex justify-end pt-2">
                <button onClick={finish} className="inline-flex items-center gap-2 h-11 px-5 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-md text-sm font-semibold" data-testid="onb-finish">
                  Naar mijn dashboard <ChevronRight size={15} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ShortcutCard({ icon, title, desc, onClick, testid }) {
  return (
    <button onClick={onClick} className="text-left card-flat p-4 hover:border-[#5BC044] hover:shadow-md transition-all" data-testid={testid}>
      <div className="text-2xl mb-2">{icon}</div>
      <div className="font-semibold text-[#1B2D5C] text-sm">{title}</div>
      <div className="text-xs text-zinc-500 mt-0.5">{desc}</div>
    </button>
  );
}
